// FIXED: was @ts-nocheck - TODO: Fix TypeScript errors in this file
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { ContractList } from '@/components/contracts/contract-list';
import { ContractForm } from '@/components/contracts/contract-form';
import { ContractDetails } from '@/components/contracts/contract-details';
import { useToast } from '@/hooks/use-toast';
import type { Contract } from '@/lib/types/contracts';
import { ContractStatus, TermUnit, ContractDocumentStatus } from '@/lib/types/enums';

// Mock data for demonstration
const mockContracts: Contract[] = [
  {
    id: '1',
    title: 'Annual Maintenance Contract',
    description: 'Comprehensive maintenance services for all properties',
    status: ContractStatus.ACTIVE,
    startDate: '2024-01-01T00:00:00Z',
    endDate: '2024-12-31T23:59:59Z',
    termLength: 12,
    termUnit: TermUnit.MONTH,
    value: 50000,
    clientId: 'client1',
    providerId: 'provider1',
    autoRenew: true,
    renewalPreference: 'automatic',
    renewalHistory: [],
    serviceHistory: [],
    client: { id: '1', name: 'Client Corp', email: 'client@example.com' },
    provider: { id: '1', name: 'Provider Inc', email: 'provider@example.com' },
    createdAt: '2023-12-15T00:00:00Z',
    updatedAt: '2023-12-15T00:00:00Z',
    terms: 'Standard maintenance terms',
    documents: [
      {
        id: 'document1',
        name: 'Master Service Agreement',
        title: 'Master Service Agreement',
        type: 'pdf',
        url: '/documents/contract1.pdf',
        uploadedAt: '2024-01-01T00:00:00Z',
        uploadedBy: { id: 'user1', name: 'Admin User' },
        size: 1024000,
        isRequired: true,
        status: ContractDocumentStatus.APPROVED
      }
    ],
    services: [
      {
        id: 'service1',
        name: 'General Maintenance',
        description: 'Regular maintenance and repairs',
        frequency: 'monthly',
        price: 2500,
        quantity: 1,
        unit: 'month'
      },
      {
        id: 'service2',
        name: 'Landscaping',
        description: 'Lawn care and garden maintenance',
        frequency: 'weekly',
        price: 1000,
        quantity: 4,
        unit: 'visit'
      }
    ],
  },
  {
    id: '2',
    title: 'Property Management Agreement',
    description: 'Full-service property management contract',
    status: ContractStatus.DRAFT,
    startDate: '2024-02-01T00:00:00Z',
    endDate: '2025-01-31T23:59:59Z',
    termLength: 12,
    termUnit: TermUnit.MONTH,
    value: 75000,
    clientId: 'client2',
    providerId: 'provider1',
    autoRenew: false,
    renewalPreference: 'manual',
    renewalHistory: [],
    serviceHistory: [],
    client: { id: '2', name: 'Client LLC', email: 'client2@example.com' },
    provider: { id: '1', name: 'Provider Inc', email: 'provider@example.com' },
    createdAt: '2024-01-15T00:00:00Z',
    updatedAt: '2024-01-15T00:00:00Z',
    terms: 'Property management standard terms',
    documents: [],
    services: [
      {
        id: 'service3',
        name: 'Property Management',
        description: 'Complete property management services',
        frequency: 'monthly',
        price: 5000,
        quantity: 1,
        unit: 'property'
      }
    ],
  },
  {
    id: '3',
    title: 'Project Management Contract',
    description: 'Professional project management services for all construction',
    status: ContractStatus.PENDING,
    startDate: '2024-03-01T00:00:00Z',
    endDate: '2025-02-28T23:59:59Z',
    termLength: 12,
    termUnit: TermUnit.MONTH,
    value: 75000,
    clientId: 'client3',
    providerId: 'provider1',
    autoRenew: false,
    renewalPreference: 'manual',
    renewalHistory: [],
    serviceHistory: [],
    client: { id: '3', name: 'Client Corp', email: 'client3@example.com' },
    provider: { id: '1', name: 'Provider Inc', email: 'provider@example.com' },
    createdAt: '2024-02-15T00:00:00Z',
    updatedAt: '2024-02-15T00:00:00Z',
    terms: 'Project management standard terms',
    documents: [],
    services: [
      {
        id: 'service4',
        name: 'Project Management',
        description: 'Complete project management services',
        frequency: 'monthly',
        price: 5000,
        quantity: 1,
        unit: 'project'
      }
    ],
  }
];

type View = 'list' | 'form' | 'details';

export default function AdminContractsPage() {
  const [view, setView] = useState<View>('list');
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const { toast } = useToast();

  const handleCreateContract = async (data: Partial<Contract>) => {
    try {
      // Here you would make an API call to create the contract
      console.log('Creating contract:', data);
      
      toast({
        title: 'Success',
        description: 'Contract created successfully',
      });
      setView('list');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to create contract',
      });
    }
  };

  const handleUpdateContract = async (data: Partial<Contract>) => {
    try {
      // Here you would make an API call to update the contract
      console.log('Updating contract:', data);
      
      toast({
        title: 'Success',
        description: 'Contract updated successfully',
      });
      setView('list');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update contract',
      });
    }
  };

  const handleDownloadDocument = async (documentUrl: string) => {
    try {
      // Here you would handle the document download
      console.log('Downloading document:', documentUrl);
      
      toast({
        title: 'Download Started',
        description: 'Your document is being downloaded',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to download document',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Contracts</h1>
        <div className="flex items-center gap-4">
          {view === 'details' && (
            <Button variant="outline" onClick={() => setView('list')}>
              Back to List
            </Button>
          )}
          {view === 'list' && (
            <Button onClick={() => {
              setSelectedContract(null);
              setView('form');
            }}>
              <Plus className="mr-2 h-4 w-4" />
              Create Contract
            </Button>
          )}
        </div>
      </div>

      {view === 'list' && (
        <ContractList
          contracts={mockContracts}
          onContractClick={(contract) => {
            setSelectedContract(contract);
            setView('details');
          }}
          onDownloadDocument={handleDownloadDocument}
        />
      )}

      {view === 'form' && (
        <ContractForm
          contract={selectedContract}
          onSubmit={selectedContract ? handleUpdateContract : handleCreateContract}
        />
      )}

      {view === 'details' && selectedContract && (
        <ContractDetails
          contract={selectedContract}
          onEdit={() => setView('form')}
          onDownloadDocument={handleDownloadDocument}
          onBack={() => setView('list')}
        />
      )}
    </div>
  );
}